import {
  faArrowFromRight,
  faDatabase,
  faEllipsisHAlt,
  faStream,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FC, ReactElement } from 'react';
import {
  mainServiceTypeLabels,
  offerStatusLabels,
  serviceTypesFilterLabels,
  serviceTypesToLowerCaseLabels,
} from 'src/apis/lng-marketing/mappings';
import type {
  LngOfferDetails,
  LngOffersOverviewParams,
  OfferStatus,
  ServiceType,
  ServiceTypeLowerCase,
  ServiceTypes,
} from 'src/apis/lng-marketing/types';
import type { LngMarketingRootRequest } from 'src/apis/lng-marketing/use-lng-marketing-root';
import {
  StatusDisplay,
  type StatusProps,
} from 'src/components/data-display/status-display';
import { Tooltip } from 'src/components/overlay/tooltip';
import type { Successful } from 'src/hooks/use-axios';
import {
  FormatLngAndSuperBundleTimerForTable,
  useTimer,
} from 'src/hooks/use-timer';
import type { LngOfferFormValues } from 'src/pages/lng/offers/create/schema';

export const OfferStatusWithTimer: FC<{
  status: OfferStatus;
  countdownEnd?: string | null;
  lngMarketingRoot: Successful<LngMarketingRootRequest>;
}> = ({ status, countdownEnd, lngMarketingRoot }) => {
  return (
    <StatusDisplay
      variant={offerStatusVariantMap[status]}
      status={offerStatusLabels[status]}
      appendix={
        countdownEnd && (
          <Countdown
            timestamp={countdownEnd}
            lngMarketingRoot={lngMarketingRoot}
          />
        )
      }
    />
  );
};

export const offerStatusVariantMap: Record<
  OfferStatus,
  StatusProps['variant']
> = {
  OPEN: 'ready',
  FINISHED: 'success',
  ADDITIONAL_SERVICE_BOOKING_OPEN: 'success',
  CANCELLED: 'inactive',
  UNSUCCESSFUL: 'error',
  REJECTED: 'error',
  FAILED: 'error',
  CREATED: 'warning',
  PUBLISHED: 'warning',
  WAITING_FOR_NEXT_ROUND: 'warning',
  ALLOCATION_PENDING: 'warning',
  APPROVAL_PENDING: 'warning',
};

const Countdown: FC<{
  timestamp: string;
  lngMarketingRoot: Successful<LngMarketingRootRequest>;
}> = ({ timestamp, lngMarketingRoot }) => {
  const timer = useTimer(timestamp);

  if (!timer) return null;
  return (
    <FormatLngAndSuperBundleTimerForTable
      timer={timer}
      lngMarketingRoot={lngMarketingRoot}
    />
  );
};

export const AvailableServices: FC<{
  services: Array<ServiceType>;
}> = ({ services }) => {
  const correctOrderServices: Array<ServiceType> = [
    'UNLOADING',
    'STORAGE',
    'REGASIFICATION',
    'TRANSPORT',
  ];
  return (
    <div className="grid grid-flow-col items-center justify-between">
      {correctOrderServices.map((service) => (
        <span key={service}>{getIconWithTooltip(service, services)}</span>
      ))}
    </div>
  );
};

const getIconWithTooltip = (
  service: ServiceType,
  services: Array<ServiceType>
) => {
  switch (service) {
    case 'UNLOADING':
      return services.includes('UNLOADING') ? (
        <Tooltip content={<small>Unloading</small>}>
          {(targetProps) => (
            <FontAwesomeIcon icon={faArrowFromRight} {...targetProps} />
          )}
        </Tooltip>
      ) : (
        <Tooltip content={<small>Unloading</small>}>
          {(targetProps) => (
            <div
              className={`
                text-foreground-surface-subdued
                legacy:text-legacy-inactive
              `}
            >
              <FontAwesomeIcon icon={faArrowFromRight} {...targetProps} />
            </div>
          )}
        </Tooltip>
      );
    case 'STORAGE':
      return services.includes('STORAGE') ? (
        <Tooltip content={<small>Storage</small>}>
          {(targetProps) => (
            <FontAwesomeIcon icon={faDatabase} {...targetProps} />
          )}
        </Tooltip>
      ) : (
        <Tooltip content={<small>Storage</small>}>
          {(targetProps) => (
            <div
              className={`
                text-foreground-surface-subdued
                legacy:text-legacy-inactive
              `}
            >
              <FontAwesomeIcon icon={faDatabase} {...targetProps} />
            </div>
          )}
        </Tooltip>
      );
    case 'REGASIFICATION':
      return services.includes('REGASIFICATION') ? (
        <Tooltip content={<small>Regasification</small>}>
          {(targetProps) => (
            <FontAwesomeIcon icon={faEllipsisHAlt} {...targetProps} />
          )}
        </Tooltip>
      ) : (
        <Tooltip content={<small>Regasification</small>}>
          {(targetProps) => (
            <div
              className={`
                text-foreground-surface-subdued
                legacy:text-legacy-inactive
              `}
            >
              <FontAwesomeIcon icon={faEllipsisHAlt} {...targetProps} />
            </div>
          )}
        </Tooltip>
      );
    case 'TRANSPORT':
      return services.includes('TRANSPORT') ? (
        <Tooltip content={<small>Transport</small>}>
          {(targetProps) => (
            <FontAwesomeIcon icon={faStream} {...targetProps} />
          )}
        </Tooltip>
      ) : (
        <Tooltip content={<small>Transport</small>}>
          {(targetProps) => (
            <div
              className={`
                text-foreground-surface-subdued
                legacy:text-legacy-inactive
              `}
            >
              <FontAwesomeIcon icon={faStream} {...targetProps} />
            </div>
          )}
        </Tooltip>
      );
  }
};

export const serviceTypeIcons: Record<ServiceType, ReactElement> = {
  UNLOADING: <FontAwesomeIcon icon={faArrowFromRight} />,
  STORAGE: <FontAwesomeIcon icon={faDatabase} />,
  REGASIFICATION: <FontAwesomeIcon icon={faEllipsisHAlt} />,
  TRANSPORT: <FontAwesomeIcon icon={faStream} />,
};

export const serviceTypeIconsLowercase: Record<
  ServiceTypeLowerCase,
  ReactElement
> = {
  unloading: <FontAwesomeIcon icon={faArrowFromRight} />,
  storage: <FontAwesomeIcon icon={faDatabase} />,
  regasification: <FontAwesomeIcon icon={faEllipsisHAlt} />,
  transport: <FontAwesomeIcon icon={faStream} />,
};

export const additionalServiceTypeIcons: Record<ServiceTypes, ReactElement> = {
  UnloadingServiceJson: <FontAwesomeIcon icon={faArrowFromRight} />,
  StorageServiceJson: <FontAwesomeIcon icon={faDatabase} />,
  RegasificationServiceJson: <FontAwesomeIcon icon={faEllipsisHAlt} />,
  TransportServiceJson: <FontAwesomeIcon icon={faStream} />,
};

export const lngOffersEmptyParams: LngOffersOverviewParams = {
  limit: 0, // we just need the create link
};

export const convertMinutesToDate = (minutes: number) => {
  if (minutes < 60) {
    return `${minutes}min`;
  } else if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}min`;
  } else {
    const days = Math.floor(minutes / 1440);
    const remainingMinutes = minutes % 1440;
    const hours = Math.floor(remainingMinutes / 60);
    const mins = remainingMinutes % 60;
    return `${days}d ${hours > 0 ? `${hours}h` : ''} ${mins}min`;
  }
};

export const convertHoursMinutesToTime = (hours: number, minutes: number) => {
  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}`;
};

export const convertTimeInMinutesToDaysHoursMinutes = (
  timeInMinutes: number
) => {
  const days = Math.floor(timeInMinutes / (60 * 24));
  let remainingMinutes = timeInMinutes % (60 * 24);
  const hours = Math.floor(remainingMinutes / 60);
  remainingMinutes = remainingMinutes % 60;
  const minutes = remainingMinutes % 60;
  return { days, hours, minutes };
};

export const convertTimeInMinutesToHoursMinutes = (timeInMinutes: number) => {
  const hours = Math.floor(timeInMinutes / 60);
  let remainingMinutes = timeInMinutes % 60;
  const minutes = remainingMinutes % 60;
  return { hours, minutes };
};

export function getLabelForUnitOfThePriceInForm(values: LngOfferFormValues) {
  if (!values.bidOnUnitOfService) {
    return '';
  }

  if (
    values[values.bidOnUnitOfService]?.displayName &&
    values[values.bidOnUnitOfService]!.displayName!.trim().split(/\s+/).length <
      4
  ) {
    return values[values.bidOnUnitOfService]?.displayName;
  }
  return mainServiceTypeLabels[values.bidOnUnitOfService];
}

export function getLabelForUnitOfThePrice(data: LngOfferDetails) {
  if (!data.unitPriceConfiguration) {
    return '';
  }

  if (
    data.mainServices[
      serviceTypesToLowerCaseLabels[data.unitPriceConfiguration.serviceType]
    ]?.displayName &&
    data.mainServices[
      serviceTypesToLowerCaseLabels[data.unitPriceConfiguration.serviceType]
    ]!.displayName!.trim().split(/\s+/).length < 4
  ) {
    return data.mainServices[
      serviceTypesToLowerCaseLabels[data.unitPriceConfiguration.serviceType]
    ]?.displayName;
  }

  return serviceTypesFilterLabels[data.unitPriceConfiguration.serviceType];
}

export const filterOutPastDates = (date: string): string | null => {
  // if the date is in the past, return null to not set the date
  if (new Date(date) < new Date()) {
    return null;
  }
  return date;
};
